import { computed, Injectable, signal } from '@angular/core';
import { updateSignalState } from '../../../../shared/utils/update-signal';
import {
  CentrationData,
  ConsultationStep,
  PrescriptionData,
} from '../apis/consultation/interfaces/consultation-step.interface';
import {
  ConsultationSummaryItem,
  RecommendedLensGroups,
  SecondaryRecommendation,
} from '../apis/consultation/interfaces/consultation.interface';

interface ConsultationStateModel {
  consultation_history: ConsultationStep[];
  consultation_id: string;
  consultation_summary: ConsultationSummaryItem[];
  recommended_lens: RecommendedLensGroups | null;
  is_finished: boolean;
  is_prescription_valid: boolean;
  consultation_step_title: string | null;
  use_case_recommendations: string[];
  secondary_recommendations: SecondaryRecommendation[] | null;
  secondary_recommendations_loading: boolean;
}

@Injectable({ providedIn: 'root' })
export class ConsultationState {
  private readonly initialState: ConsultationStateModel = {
    consultation_history: [],
    consultation_id: '',
    consultation_summary: [],
    recommended_lens: null,
    is_finished: false,
    is_prescription_valid: true,
    consultation_step_title: null,
    use_case_recommendations: [],
    secondary_recommendations: null,
    secondary_recommendations_loading: false,
  };

  private consultationState = signal<ConsultationStateModel>(this.initialState);

  private requestTrigger = signal(0);

  public consultationId = computed(
    () => this.consultationState().consultation_id,
  );
  public consultationHistory = computed(
    () => this.consultationState().consultation_history,
  );
  public consultationSummary = computed(
    () => this.consultationState().consultation_summary,
  );
  public trigger = computed(() => this.requestTrigger());

  public recommendedLens = computed(
    () => this.consultationState().recommended_lens,
  );

  public isFinished = computed(() => this.consultationState().is_finished);
  public isPrescriptionValid = computed(
    () => this.consultationState().is_prescription_valid,
  );
  public consultationStepTitle = computed(
    () => this.consultationState().consultation_step_title,
  );
  public useCaseRecommendations = computed(
    () => this.consultationState().use_case_recommendations,
  );

  public secondaryRecommendations = computed(
    () => this.consultationState().secondary_recommendations,
  );

  public secondaryRecommendationsLoading = computed(
    () => this.consultationState().secondary_recommendations_loading,
  );

  public prescriptionData = computed(() => {
    const history = this.consultationState().consultation_history;
    const prescriptionStep = history.find(
      (step) => step.content.kind === 'prescription_data',
    );
    const prescription = prescriptionStep?.content.prescription;
    if (!prescription) {
      return undefined;
    }

    const centration = prescriptionStep.content.centration;
    return {
      prescription,
      centration,
    } as { prescription: PrescriptionData; centration?: CentrationData | null };
  });

  public appendStepAndTrigger(step: ConsultationStep) {
    this.appendStep(step);
    this.requestTrigger.update((v) => v + 1);
  }

  public appendStep(step: ConsultationStep) {
    updateSignalState(this.consultationState, 'consultation_history', [
      ...this.consultationState().consultation_history,
      step,
    ]);
  }

  public updateStepAtIndex(stepIndex: number, updatedStep: ConsultationStep) {
    const history = this.consultationState().consultation_history;
    if (stepIndex < 0 || stepIndex >= history.length) {
      return;
    }

    updateSignalState(this.consultationState, 'consultation_history', [
      ...history.slice(0, stepIndex),
      updatedStep,
      ...history.slice(stepIndex + 1),
    ]);
  }

  public updateLastStepAndTrigger(updatedStep: ConsultationStep) {
    const history = this.consultationState().consultation_history;
    if (!history.length) {
      return;
    }

    updateSignalState(this.consultationState, 'consultation_history', [
      ...history.slice(0, -1),
      updatedStep,
    ]);
    this.requestTrigger.update((v) => v + 1);
  }

  public updateConsultationId(id: string) {
    updateSignalState(this.consultationState, 'consultation_id', id);
  }

  public loadConsultationHistory(consultationHistory: ConsultationStep[]) {
    updateSignalState(
      this.consultationState,
      'consultation_history',
      consultationHistory,
    );
  }

  public updateConsultationSummary(summary: ConsultationSummaryItem[]) {
    updateSignalState(this.consultationState, 'consultation_summary', summary);
  }

  public updateResponseData(data: {
    recommended_lens: RecommendedLensGroups | null;
    is_finished: boolean;
    is_prescription_valid: boolean;
    consultation_step_title: string;
    use_cases_recommendations: string[];
  }) {
    this.consultationState.update((state) => ({
      ...state,
      recommended_lens: data.recommended_lens,
      is_finished: data.is_finished,
      is_prescription_valid: data.is_prescription_valid,
      consultation_step_title: data.consultation_step_title,
      use_case_recommendations: data.use_cases_recommendations,
    }));
  }

  public resetState() {
    this.consultationState.set(this.initialState);
    this.requestTrigger.set(0);
  }

  public removeStepsAfterIndex(index: number) {
    updateSignalState(
      this.consultationState,
      'consultation_history',
      this.consultationState().consultation_history.slice(0, index + 1),
    );
  }

  public updateSecondaryRecommendations(
    recommendations: SecondaryRecommendation[] | null,
  ) {
    this.consultationState.update((state) => ({
      ...state,
      secondary_recommendations: recommendations,
    }));
  }

  public setSecondaryRecommendationsLoading(loading: boolean) {
    this.consultationState.update((state) => ({
      ...state,
      secondary_recommendations_loading: loading,
    }));
  }
}
